// ============================================================================
// @gs/i18n/core — initialisation i18next SANS React (D10, V1 gel du gabarit)
// ============================================================================
// Pourquoi ce point d'entree existe : `config.ts` (initI18n) importe
// `react-i18next` et suppose un peer React. Un serveur SSR (api-portail, pages
// publiques /p/:qr) n'en a pas — il avait donc FORKE l'init i18next dans son
// app (`apps/api-portail/src/i18n/index.ts`). Un fork est tolerable ; deux
// forks a reconcilier ne le sont pas. Ce module porte tout ce qui n'a pas
// besoin de React, et `initI18n` le consomme a son tour : une seule init,
// deux facades.
//
// Ce que ce module N'IMPORTE PAS, par contrat : react, react-i18next,
// ./localeContext (React), et rien qui touche window/document/localStorage.
// La detection de locale navigateur vit dans `config.ts` (SPA) ; en SSR la
// locale est une donnee de REQUETE (cookie / Accept-Language), jamais un etat
// global — l'appelant passe `initialLocale` et lit ses traductions par
// `getFixedT(locale)`, thread-safe par requete.
//
// ESCAPE : `escapeValue` vaut TRUE par defaut ici. En SSR il n'y a pas de React
// pour echapper les interpolations (XSS) ; c'est la facade React (`initI18n`)
// qui le repasse a false parce que React echappe deja.

import i18next from 'i18next';
import type { InitOptions, Resource } from 'i18next';
import type { i18n as I18nextInstance } from 'i18next';

import { DEFAULT_LOCALE } from './supportedLocales';
import type { SupportedLocale } from './supportedLocales';

import commonFr from './locales/common.fr.json';
import commonEn from './locales/common.en.json';

/**
 * Instance i18next rendue par `initI18nCore` — TYPE NOMME (D15).
 *
 * Sans ce nom, `const i18n = initI18nCore(...)` s'infere en `typeof i18next`,
 * et `tsc` doit alors nommer le module i18next par son realpath. Dans un
 * worktree dont `node_modules` est une JONCTION (mode par defaut de
 * `npm run git:new`), ce realpath sort de l'arbre compile et le build casse :
 * `TS2742: The inferred type of 'i18n' cannot be named without a reference to
 * '@gs/i18n/node_modules/i18next'`. Mesure du 2026-08-26 : meme worktree, meme
 * SHA, jonctionne = rouge (inv, ug) / `npm ci` en place = vert.
 *
 * La reparation est ICI et nulle part ailleurs : une interface declaree dans
 * `@gs/i18n` est nommable par toute app qui importe deja `@gs/i18n`. AUCUNE app
 * n'a plus a nommer i18next, ni a annoter son `const i18n`.
 */
export interface GsI18nCore extends I18nextInstance {}

/** Plugin i18next (module `use()`) — typage derive de l'instance, jamais copie. */
export type GsI18nPlugin = Parameters<I18nextInstance['use']>[0];

export interface InitI18nCoreOptions {
  /** Namespace de l'app (`ug`, `inv`, `portail`...) — devient le defaultNS. */
  appNamespace: string;
  /** Catalogues par locale : { fr: { <ns>: {...} }, en: { <ns>: {...} } }. */
  resources: Record<SupportedLocale, Record<string, Record<string, unknown>>>;
  /** Locale de depart. Defaut : FR. En SSR, la locale reelle est par requete. */
  initialLocale?: SupportedLocale;
  /**
   * i18next echappe-t-il les interpolations ? Defaut TRUE (SSR, pas de React).
   * La facade React le met a false — React echappe deja.
   */
  escapeValue?: boolean;
  /**
   * Fusionner le namespace `common` de @gs/i18n. Defaut TRUE : c'est ce qui
   * rend la parite `common` REELLE au lieu d'etre une revue manuelle.
   */
  includeCommon?: boolean;
  /** Plugins i18next (la facade React passe `initReactI18next`). */
  use?: GsI18nPlugin[];
  /** Options i18next additionnelles, fusionnees en DERNIER (ex: `react`). */
  extra?: InitOptions;
}

/**
 * Initialise i18next core (aucune dependance React) et rend l'instance.
 * Consomme tel quel par un serveur SSR, et par `initI18n` (facade React).
 */
export function initI18nCore(options: InitI18nCoreOptions): GsI18nCore {
  const {
    appNamespace,
    resources,
    initialLocale = DEFAULT_LOCALE,
    escapeValue = true,
    includeCommon = true,
    use = [],
    extra = {},
  } = options;

  const mergedResources: Resource = includeCommon
    ? {
        fr: { common: commonFr, ...resources.fr },
        en: { common: commonEn, ...resources.en },
      }
    : { fr: { ...resources.fr }, en: { ...resources.en } };

  const namespaces = includeCommon ? ['common', appNamespace] : [appNamespace];

  const initOptions: InitOptions = {
    resources: mergedResources,
    lng: initialLocale,
    fallbackLng: DEFAULT_LOCALE,
    defaultNS: appNamespace,
    ns: namespaces,
    interpolation: { escapeValue },
    ...extra,
  };

  let instance: GsI18nCore = i18next;
  for (const plugin of use) {
    instance = instance.use(plugin);
  }
  void instance.init(initOptions);
  return instance;
}
